/**
 * person-match.ts — the ONE place a stored person reference (user id, owner
 * string, submitter label, @mention token) resolves to a WS1 person record.
 *
 * Historical ticket data stores people three different ways — "rcastillo",
 * "rcastillo@stevensonsystems.com", "Ryan Castillo" (spec R8.2's complaint).
 * Every chip and picker resolves through here so all three render as the same
 * person, and unresolvable labels degrade to the raw text instead of guessing.
 */

import type { RoadmapPerson } from '@/api/endpoints/roadmap';

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** "Ryan Castillo" → "ryancastillo" — matches mention tokens typed from a name. */
function compactName(name: string): string {
  return name.replace(/\s+/g, '').toLowerCase();
}

function emailLocalPart(email: string): string {
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

/** Person by main-app user id — the strongest identity a row can carry. */
export function findPersonByUserId(
  people: readonly RoadmapPerson[],
  userId: number | null | undefined,
): RoadmapPerson | null {
  if (userId == null) return null;
  return people.find((person) => person.userId === userId) ?? null;
}

/**
 * Person by any stored label shape: full email, email local-part, username,
 * display name, or a whitespace-compacted display name (how mention tokens
 * are typed). Case-insensitive, exact match only — fuzzy matching here would
 * re-create the constructed-identity bug class R8.8 exists to kill.
 */
export function findPersonByLabel(
  people: readonly RoadmapPerson[],
  label: string | null | undefined,
): RoadmapPerson | null {
  const value = clean(label)?.toLowerCase();
  if (!value) return null;
  return (
    people.find((person) => {
      const email = person.email.toLowerCase();
      return (
        (email !== '' && (email === value || emailLocalPart(email) === value)) ||
        (person.username !== '' && person.username.toLowerCase() === value) ||
        (person.displayName !== '' &&
          (person.displayName.toLowerCase() === value || compactName(person.displayName) === value))
      );
    }) ?? null
  );
}

function sameMailbox(person: RoadmapPerson, email: string): boolean {
  return person.email.trim().toLowerCase() === email;
}

/**
 * Person by user id first, else by label — the usual pair a ticket row stores.
 *
 * Pass `snapshotEmail` when the row recorded a mailbox. Local, stage, and
 * prod reuse the same numeric user ids for different people, and the shared
 * roster sync last-write-wins. A user-id hit whose mailbox disagrees with
 * the snapshot is ignored, so one environment cannot relabel another's tickets.
 */
export function resolvePerson(
  people: readonly RoadmapPerson[],
  userId: number | null | undefined,
  label: string | null | undefined,
  snapshotEmail?: string | null,
): RoadmapPerson | null {
  const byId = findPersonByUserId(people, userId);
  const email = clean(snapshotEmail)?.toLowerCase() ?? null;
  if (byId && (email == null || sameMailbox(byId, email))) return byId;

  const byLabel = findPersonByLabel(people, label) ?? (email ? findPersonByLabel(people, email) : null);
  if (!byLabel) return null;
  // The only remaining mailbox match is an inactive duplicate from another
  // environment. Keep the snapshot text instead of marking a current person
  // as no longer at SSI.
  if (
    !byLabel.active &&
    email != null &&
    sameMailbox(byLabel, email) &&
    byId != null &&
    byId.userId !== byLabel.userId
  ) {
    return null;
  }
  return byLabel;
}

/**
 * Fragment search for pickers: case-insensitive substring across display
 * name, username, email, and email local-part. Empty query matches everyone.
 */
export function personMatchesQuery(person: RoadmapPerson, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const email = person.email.toLowerCase();
  return (
    person.displayName.toLowerCase().includes(q) ||
    person.username.toLowerCase().includes(q) ||
    email.includes(q) ||
    emailLocalPart(email).includes(q)
  );
}

/**
 * The token written into a comment body for an @mention — email local-part
 * when the person has one (matches the server's mention parser), else the
 * compacted display name.
 */
export function mentionToken(person: RoadmapPerson): string {
  const email = clean(person.email);
  if (email) return emailLocalPart(email);
  return person.displayName.replace(/\s+/g, '');
}

/** Splits an "owner / eng owner" cell label back into its person labels. */
export function splitPersonLabels(label: string | null | undefined): string[] {
  const value = clean(label);
  if (!value) return [];
  return value
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/** Up-to-two-letter initials for the avatar: first letters of first + last word. */
export function personInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0][0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
